import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../../ui/UIElements/Icon";
import {
  useCartRecoveryRuleDetail,
  useCartRecoveryChannels,
  useUpdateCartRecoveryChannel,
  useDeleteCartRecoveryRule,
} from "../../../../hooks/useCartRecovery";
import DeleteConfirmationModal from "../../../ui/Modals/DeleteConfirmationModal";
import CartRecoveryForm from "./CartRecoveryForm";
import { stripHtml } from "../../../../constants/campaignConstants";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { Switch } from "../../../ui/UIElements/Switch";

const getPrimaryDelayMinutes = (rule) => {
  const times = rule?.times;
  if (!Array.isArray(times) || times.length === 0) return null;
  const first = times[0];
  return typeof first === "number" ? first : null;
};

const getChannelIconName = (channel) => {
  const c = (channel || "").toLowerCase();
  if (c === "email") return "Mail";
  if (c === "sms") return "MessageSquare";
  if (c === "notification") return "Bell";
  if (c === "webhook") return "Webhook";
  return "Send";
};

const DEFAULT_EMAIL_BODY = "<p><br></p>";

const buildChannelUpdatePayload = (ch, enabled) => {
  const channel = (ch.channel || "email").toLowerCase();
  const body =
    channel === "email"
      ? ch.body || DEFAULT_EMAIL_BODY
      : stripHtml(ch.body || "").trim();
  return {
    enabled,
    channel,
    body,
    title: (ch.title || "").trim(),
  };
};

/**
 * Email device preview — matches CampaignDetailPage structure (campaigns detail preview).
 * No CTA button in cart recovery preview.
 */
const EmailCampaignStylePreview = ({ title, bodyText, tCamp, t }) => {
  return (
    <div className="w-full max-w-[280px]">
      <div className="rounded-[2.5rem] border-4 border-gray-800 bg-gray-950 p-2">
        <div className="relative">
          <div className="absolute top-0 left-1/2 z-10 flex h-6 w-20 -translate-x-1/2 transform items-center justify-center rounded-b-xl bg-gray-950">
            <div className="h-2 w-2 rounded-full bg-gray-800" />
            <div className="ml-1 h-2 w-2 rounded-full bg-gray-800" />
          </div>
          <div className="overflow-hidden rounded-[2rem] bg-white">
            <div className="flex items-center justify-between bg-gray-100 px-4 py-2 text-xs text-gray-600">
              <span>9:41</span>
              <div className="flex items-center gap-1">
                <div className="h-2 w-4 rounded-sm bg-gray-400" />
                <div className="h-2 w-4 rounded-sm bg-gray-400" />
              </div>
            </div>
            <div className="flex items-center justify-between bg-purple-600 px-4 py-3">
              <Icon name="ArrowLeft" className="h-5 w-5 text-white" />
              <span className="font-medium text-white">
                {tCamp("detail.preview.inbox")}
              </span>
              <Icon name="Search" className="h-5 w-5 text-white" />
            </div>
            <div className="min-h-[500px] p-4">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
                  <span className="text-sm font-bold text-purple-600">
                    {t("detail.preview.defaultSenderInitial")}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {t("detail.preview.defaultSender")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {tCamp("detail.preview.toMe")}
                  </p>
                </div>
                <span className="text-xs text-gray-400">
                  {tCamp("detail.preview.now")}
                </span>
              </div>
              <h3 className="mb-3 text-sm font-semibold text-gray-900">
                {title || tCamp("detail.preview.defaultSubject")}
              </h3>
              <div className="space-y-2 text-xs text-gray-600">
                {bodyText ? (
                  <p className="whitespace-pre-wrap">
                    {bodyText.substring(0, 200)}
                    {bodyText.length > 200 ? "..." : ""}
                  </p>
                ) : (
                  <>
                    <p>{tCamp("detail.preview.fallbackBodyLine1")}</p>
                    <p>{tCamp("detail.preview.fallbackBodyLine2")}</p>
                    <p>{tCamp("detail.preview.fallbackBodyLine3")}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SmsDevicePreview = ({ title, bodyText, t }) => (
  <div className="w-full max-w-[280px]">
    <div className="rounded-[2.5rem] border-4 border-gray-800 bg-gray-950 p-2">
      <div className="relative">
        <div className="absolute top-0 left-1/2 z-10 flex h-6 w-20 -translate-x-1/2 transform items-center justify-center rounded-b-xl bg-gray-950">
          <div className="h-2 w-2 rounded-full bg-gray-800" />
          <div className="ml-1 h-2 w-2 rounded-full bg-gray-800" />
        </div>
        <div className="flex min-h-[520px] flex-col overflow-hidden rounded-[2rem] bg-[#1c1c1e]">
          <div className="flex items-center justify-between bg-black/40 px-4 py-2 text-[10px] text-gray-400">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <div className="h-2 w-3 rounded-sm bg-gray-500" />
              <div className="h-2 w-4 rounded-sm bg-gray-500" />
            </div>
          </div>
          <div className="flex items-center justify-between border-b border-gray-800/80 px-4 py-3">
            <Icon name="ChevronLeft" className="h-5 w-5 text-blue-400" />
            <div className="text-center">
              <p className="text-xs font-semibold text-white">
                {t("detail.preview.smsPlaceholder")}
              </p>
              <p className="max-w-[140px] truncate text-[10px] text-gray-500">
                {title || "—"}
              </p>
            </div>
            <Icon name="MoreVertical" className="h-5 w-5 text-gray-500" />
          </div>
          <div className="flex min-h-0 flex-1 flex-col px-3 py-4">
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-gray-700 px-3 py-2">
                <p className="text-[11px] font-medium text-gray-200">
                  {title || "—"}
                </p>
                <p className="mt-1 text-[11px] leading-relaxed whitespace-pre-wrap text-gray-100">
                  {bodyText || ""}
                </p>
              </div>
            </div>
            <div className="flex-1" aria-hidden="true" />
          </div>
          <div className="flex justify-center pt-1 pb-3">
            <div className="h-1 w-28 rounded-full bg-gray-600/80" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const NotificationDevicePreview = ({ title, bodyText, t }) => (
  <div className="w-full max-w-[280px]">
    <div className="rounded-[2.5rem] border-4 border-gray-800 bg-gray-950 p-2">
      <div className="relative">
        <div className="absolute top-0 left-1/2 z-10 flex h-6 w-20 -translate-x-1/2 transform items-center justify-center rounded-b-xl bg-gray-950">
          <div className="h-2 w-2 rounded-full bg-gray-800" />
          <div className="ml-1 h-2 w-2 rounded-full bg-gray-800" />
        </div>
        <div className="flex min-h-[520px] flex-col overflow-hidden rounded-[2rem] bg-gradient-to-b from-slate-900 via-indigo-950 to-black">
          <div className="flex items-center justify-between px-4 pt-3 pb-2 text-[10px] text-white/80">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <div className="h-2 w-3 rounded-sm bg-white/30" />
              <div className="h-2 w-4 rounded-sm bg-white/30" />
            </div>
          </div>
          <div className="px-4 pb-2 text-center">
            <p className="text-5xl font-extralight tracking-tight text-white/90">
              9:41
            </p>
          </div>
          <div className="flex min-h-0 flex-1 flex-col px-3 pt-4">
            <div className="rounded-2xl border border-white/10 bg-white/10 p-3 shadow-lg backdrop-blur-md">
              <div className="flex gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-500">
                  <span className="text-xs font-bold text-white">
                    {t("detail.preview.defaultSenderInitial")}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold text-white">
                      {t("detail.preview.notificationApp")}
                    </p>
                    <span className="shrink-0 text-[10px] text-white/60">
                      {t("detail.preview.notificationNow")}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs font-medium text-white">
                    {title || "—"}
                  </p>
                  <p className="mt-1 text-[11px] leading-snug text-white/85">
                    {bodyText || ""}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex-1" aria-hidden="true" />
          </div>
          <div className="flex justify-center pt-2 pb-3">
            <div className="h-1 w-28 rounded-full bg-white/20" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SelectedChannelDevicePreview = ({ channel, title, body, t, tCamp }) => {
  const raw = channel || "";
  const c = raw.toLowerCase();
  const bodyText = stripHtml(body || "");
  if (c === "email") {
    return (
      <EmailCampaignStylePreview
        title={title}
        bodyText={bodyText}
        tCamp={tCamp}
        t={t}
      />
    );
  }
  if (c === "sms") {
    return <SmsDevicePreview title={title} bodyText={bodyText} t={t} />;
  }
  if (c === "notification") {
    return (
      <NotificationDevicePreview title={title} bodyText={bodyText} t={t} />
    );
  }
  return (
    <div className="w-full max-w-[280px] rounded-lg border border-gray-700 bg-gray-800/50 p-4">
      <p className="text-center text-xs text-gray-400">
        {bodyText || stripHtml(body) || "—"}
      </p>
    </div>
  );
};

const CartRecoveryDetailPage = ({ initialRule = null }) => {
  const { t } = useTranslation("cartRecovery");
  const { t: tCamp } = useTranslation("campaigns");
  const { t: tCommon } = useTranslation("common");
  const toast = useToast();
  const { id } = useParams();
  const ruleId = parseInt(id, 10);
  const navigate = useNavigate();
  const location = useLocation();
  const ruleFromState = location.state?.rule;
  const seedRule = initialRule || ruleFromState;

  const {
    data: ruleFetched,
    isLoading: isRuleLoading,
    isError,
    error,
    refetch: refetchRule,
  } = useCartRecoveryRuleDetail(ruleId, seedRule);

  const rule = ruleFetched ?? seedRule;

  const {
    data: channels = [],
    isLoading: isChannelsLoading,
    isError: isChannelsError,
    error: channelsError,
    refetch: refetchChannels,
  } = useCartRecoveryChannels(ruleId, Boolean(ruleId));

  const updateChannelMutation = useUpdateCartRecoveryChannel();
  const deleteRuleMutation = useDeleteCartRecoveryRule();
  const [channelToggleId, setChannelToggleId] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const visibleChannels = useMemo(
    () =>
      channels.filter(
        (c) => (c.channel || "").toLowerCase() !== "notification",
      ),
    [channels],
  );

  const [selectedChannelId, setSelectedChannelId] = useState(null);

  useEffect(() => {
    if (visibleChannels.length === 0) {
      setSelectedChannelId(null);
      return;
    }
    setSelectedChannelId((prev) => {
      if (prev != null && visibleChannels.some((c) => c.id === prev)) {
        return prev;
      }
      return visibleChannels[0].id;
    });
  }, [visibleChannels]);

  const selectedChannel = useMemo(() => {
    if (!visibleChannels.length) return null;
    return (
      visibleChannels.find((c) => c.id === selectedChannelId) ||
      visibleChannels[0] ||
      null
    );
  }, [visibleChannels, selectedChannelId]);

  const handleBack = () => {
    navigate("/growth/cart-recovery");
  };

  const handleConfirmDeleteRule = async () => {
    if (!ruleId) return;
    try {
      await deleteRuleMutation.mutateAsync(ruleId);
      handleApiSuccess(t("detail.toasts.deleteSuccess"), toast, {
        title: tCommon("states.success"),
      });
      setIsDeleteModalOpen(false);
      navigate("/growth/cart-recovery");
    } catch (err) {
      handleApiError(err, toast, {
        title: tCommon("states.error"),
        defaultMessage: t("detail.toasts.deleteError"),
      });
    }
  };

  const handleChannelEnabledChange = async (ch, checked) => {
    if (!ruleId || ch?.id == null) return;
    const prevEnabled = ch.enabled !== false;
    if (checked === prevEnabled) return;
    setChannelToggleId(ch.id);
    try {
      await updateChannelMutation.mutateAsync({
        ruleId,
        channelId: ch.id,
        data: buildChannelUpdatePayload(ch, checked),
      });
      await refetchChannels();
      handleApiSuccess(t("detail.channels.toasts.updateSuccess"), toast, {
        title: tCommon("states.success"),
      });
    } catch (error) {
      handleApiError(error, toast, {
        title: tCommon("states.error"),
        defaultMessage: t("detail.channels.toasts.updateError"),
      });
    } finally {
      setChannelToggleId(null);
    }
  };

  const getLaunchTypeLabel = (launchType) => {
    if (launchType === "date") return t("launchType.date");
    return t("launchType.times");
  };

  if (isRuleLoading && !seedRule) {
    return (
      <div className="flex min-h-96 items-center justify-center p-4">
        <div className="text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 border-purple-500 sm:mb-4 sm:h-12 sm:w-12" />
          <p className="text-sm text-gray-400 sm:text-base">
            {t("detail.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (isError && !rule) {
    return (
      <div className="px-4 py-8 text-center sm:py-12">
        <Icon
          name="ShoppingCart"
          className="mx-auto mb-3 h-8 w-8 text-gray-400 sm:mb-4 sm:h-12 sm:w-12"
        />
        <h3 className="mt-2 text-sm font-medium text-gray-300 sm:text-base">
          {t("detail.error.title")}
        </h3>
        <p className="mt-1 text-xs text-gray-400 sm:text-sm">
          {error?.message || t("detail.error.subtitle")}
        </p>
        <button
          type="button"
          onClick={handleBack}
          className="mt-3 rounded-lg bg-purple-600 px-3 py-2 text-sm text-white transition-colors hover:bg-purple-700 sm:mt-4 sm:px-4 sm:text-base"
        >
          {t("detail.actions.backToList")}
        </button>
      </div>
    );
  }

  if (!rule) {
    return null;
  }

  const ruleTitle = rule.name?.trim() || t("table.fallback.untitled");

  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-start gap-4">
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="rounded-lg p-2 transition-colors hover:bg-gray-800"
            aria-label={t("detail.actions.cancelEdit")}
          >
            <Icon name="ArrowLeft" className="h-5 w-5 text-gray-400" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold text-white sm:text-2xl">
              {t("detail.actions.edit")}
            </h1>
            <p className="mt-1 text-sm text-gray-400">{ruleTitle}</p>
          </div>
        </div>
        <CartRecoveryForm
          mode="edit"
          ruleId={ruleId}
          initialRule={rule}
          initialChannels={channels}
          onCancel={() => setIsEditing(false)}
          onSuccess={async () => {
            setIsEditing(false);
            await refetchRule();
            await refetchChannels();
          }}
        />
      </div>
    );
  }

  const delayMinutes = getPrimaryDelayMinutes(rule);
  const delayLabel =
    delayMinutes != null
      ? t("delay.minutesShort", { count: delayMinutes })
      : "—";

  const selectedTitle = selectedChannel?.title || "";
  const selectedBody = selectedChannel?.body || "";
  const selectedBodyText = stripHtml(selectedBody);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start gap-4">
        <button
          type="button"
          onClick={handleBack}
          className="rounded-lg p-2 transition-colors hover:bg-gray-800"
          aria-label={t("detail.actions.backToList")}
        >
          <Icon name="ArrowLeft" className="h-5 w-5 text-gray-400" />
        </button>
        <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-bold text-white sm:text-2xl">
                {ruleTitle}
              </h1>
              <span
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  rule.enabled
                    ? "border border-green-500/40 bg-green-500/10 text-green-400"
                    : "border border-gray-500/40 bg-gray-500/10 text-gray-400"
                }`}
              >
                {rule.enabled ? t("status.enabled") : t("status.disabled")}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-400">
              {t("detail.ruleWithId", { id: rule.id })}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={isChannelsLoading}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icon name="Pencil" className="h-4 w-4" aria-hidden="true" />
              {t("detail.actions.edit")}
            </button>
            <button
              type="button"
              onClick={() => setIsDeleteModalOpen(true)}
              disabled={isChannelsLoading || deleteRuleMutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-950/30 px-4 py-2 text-sm font-medium text-red-300 transition-colors hover:bg-red-950/50 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={t("detail.actions.deleteAria")}
            >
              <Icon name="Trash2" className="h-4 w-4" aria-hidden="true" />
              {t("detail.actions.delete")}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <div className="rounded-xl border border-gray-800 bg-gray-900">
            <div className="border-b border-gray-800 p-4">
              <div className="flex items-center gap-2">
                <Icon name="BarChart3" className="h-5 w-5 text-purple-400" />
                <h2 className="text-base font-semibold text-white">
                  {t("detail.configuration.title")}
                </h2>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <p className="mb-2 text-xs tracking-wider text-gray-500 uppercase">
                    {t("detail.configuration.type")}
                  </p>
                  <span className="text-white capitalize">
                    {(rule.type || "—").replace(/_/g, " ")}
                  </span>
                </div>
                <div>
                  <p className="mb-2 text-xs tracking-wider text-gray-500 uppercase">
                    {t("detail.configuration.launchType")}
                  </p>
                  <span className="text-white">
                    {getLaunchTypeLabel(rule.launch_type)}
                  </span>
                </div>
                <div className="sm:col-span-2">
                  <p className="mb-2 text-xs tracking-wider text-gray-500 uppercase">
                    {t("detail.configuration.delay")}
                  </p>
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" className="h-4 w-4 text-gray-400" />
                    <span className="text-white">{delayLabel}</span>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <p className="mb-2 text-xs tracking-wider text-gray-500 uppercase">
                    {t("detail.configuration.defaultMessage")}
                  </p>
                  <div className="rounded-lg bg-gray-800 p-4">
                    <p className="text-sm whitespace-pre-wrap text-gray-300">
                      {rule.description || "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-800 bg-gray-900">
            <div className="border-b border-gray-800 p-4">
              <div className="flex items-center gap-2">
                <Icon name="Mail" className="h-5 w-5 text-purple-400" />
                <h2 className="text-base font-semibold text-white">
                  {t("detail.channels.title")}
                </h2>
              </div>
            </div>
            <div className="p-4">
              {isChannelsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-purple-500" />
                </div>
              ) : isChannelsError ? (
                <div className="text-center">
                  <p className="text-sm text-gray-400">
                    {channelsError?.message || t("detail.error.subtitle")}
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchChannels()}
                    className="mt-3 rounded-lg bg-gray-800 px-3 py-2 text-sm text-white hover:bg-gray-700"
                  >
                    {tCommon("actions.retry")}
                  </button>
                </div>
              ) : visibleChannels.length === 0 ? (
                <div className="py-6 text-center">
                  <Icon
                    name="FileText"
                    className="mx-auto mb-2 h-10 w-10 text-gray-600"
                  />
                  <p className="text-sm text-gray-400">
                    {t("detail.channels.empty")}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="mb-2 text-xs tracking-wider text-gray-500 uppercase">
                      {t("detail.channelPicker.label")}
                    </p>
                    <div
                      className="flex flex-wrap gap-2"
                      role="tablist"
                      aria-label={t("detail.channelPicker.label")}
                    >
                      {visibleChannels.map((ch) => {
                        const iconName = getChannelIconName(ch.channel);
                        const isSelected = selectedChannel?.id === ch.id;
                        const channelOn = ch.enabled !== false;
                        return (
                          <div
                            key={ch.id}
                            className={`inline-flex max-w-full items-center gap-0 rounded-lg border text-sm transition-colors ${
                              isSelected
                                ? "border-purple-500/50 bg-purple-500/15 text-white"
                                : "border-gray-700 bg-gray-800/80 text-gray-300"
                            }`}
                          >
                            <button
                              type="button"
                              role="tab"
                              aria-selected={isSelected}
                              onClick={() => setSelectedChannelId(ch.id)}
                              className="inline-flex min-w-0 flex-1 items-center gap-2 rounded-l-lg px-3 py-2 text-left transition-colors hover:bg-white/5"
                            >
                              <Icon
                                name={iconName}
                                className="h-4 w-4 flex-shrink-0 text-cyan-400"
                              />
                              <span className="capitalize">{ch.channel}</span>
                              <span
                                className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] ${
                                  channelOn
                                    ? "bg-green-500/15 text-green-400"
                                    : "bg-gray-600/40 text-gray-500"
                                }`}
                              >
                                {channelOn
                                  ? t("detail.channels.channelEnabled")
                                  : t("detail.channels.channelDisabled")}
                              </span>
                            </button>
                            <div
                              className="flex shrink-0 items-center border-l border-gray-700/80 px-2 py-1"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <Switch
                                checked={channelOn}
                                disabled={channelToggleId === ch.id}
                                onCheckedChange={(next) =>
                                  handleChannelEnabledChange(ch, next)
                                }
                                aria-label={t(
                                  "detail.channels.toggleChannelAria",
                                  { channel: ch.channel },
                                )}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {selectedChannel ? (
                    <>
                      <div>
                        <p className="mb-2 text-xs tracking-wider text-gray-500 uppercase">
                          {tCamp("detail.content.subjectLine")}
                        </p>
                        <p className="text-white">{selectedTitle || "—"}</p>
                      </div>
                      <div>
                        <p className="mb-2 text-xs tracking-wider text-gray-500 uppercase">
                          {tCamp("detail.content.bodyText")}
                        </p>
                        {selectedChannel.channel?.toLowerCase() === "email" &&
                        selectedBody?.includes("<") ? (
                          <div className="overflow-hidden rounded-lg bg-gray-800 p-4">
                            <div
                              className="max-w-none text-sm leading-relaxed text-gray-300 [&_p]:mb-2"
                              dangerouslySetInnerHTML={{
                                __html: selectedBody,
                              }}
                            />
                          </div>
                        ) : (
                          <div className="overflow-hidden rounded-lg bg-gray-800 p-4">
                            <p className="overflow-wrap-anywhere font-mono text-sm break-words whitespace-pre-wrap text-gray-300">
                              {selectedBodyText || "—"}
                            </p>
                          </div>
                        )}
                      </div>
                      {selectedChannel.webhook ? (
                        <div>
                          <p className="mb-2 text-xs tracking-wider text-gray-500 uppercase">
                            {t("detail.channels.webhook")}
                          </p>
                          <p className="text-sm break-all text-gray-400">
                            {selectedChannel.webhook}
                          </p>
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="xl:col-span-1">
          <div className="rounded-xl border border-gray-800 bg-gray-900 xl:sticky xl:top-6">
            <div className="border-b border-gray-800 p-4">
              <div className="flex items-center gap-2">
                <Icon name="Smartphone" className="h-5 w-5 text-gray-400" />
                <h2 className="text-base font-semibold text-white">
                  {tCamp("detail.preview.title")}
                </h2>
              </div>
            </div>
            <div className="flex justify-center p-4">
              {isChannelsLoading ? (
                <div className="flex h-64 items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-purple-500" />
                </div>
              ) : selectedChannel ? (
                <SelectedChannelDevicePreview
                  channel={selectedChannel.channel}
                  title={selectedTitle}
                  body={selectedBody}
                  t={t}
                  tCamp={tCamp}
                />
              ) : (
                <div className="py-8 text-center text-sm text-gray-500">
                  {t("detail.channels.empty")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDeleteRule}
        itemName={ruleTitle}
        itemType={t("detail.deleteModal.itemType")}
        title={t("detail.deleteModal.title")}
        subtitle={t("detail.deleteModal.subtitle")}
        confirmButtonText={t("detail.deleteModal.confirm")}
        isLoading={deleteRuleMutation.isPending}
      />
    </div>
  );
};

export default CartRecoveryDetailPage;
